<template>
  <div class="chart-toolbox border-0 border-solid border-gray-200 bg-white p-2">
    <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
        <!-- Modo -->
        <div class="flex items-center gap-1.5">
          <span class="text-sm font-medium text-body">Modo:</span>
          <button
            type="button"
            class="border border-gray-300  px-2 py-1 text-sm font-medium transition-colors"
            :class="mode === 'horizontal' ?  'bg-black text-white' : 'text-gray hover:bg-gray-50'"
            @click="$emit('set-mode', 'horizontal')">
            Horizontal
          </button>
          <button
            type="button"
            class="border border-gray-300 px-2 py-1 text-sm font-medium transition-colors"
            :class="mode === 'vertical' ? ' bg-black text-white' : 'text-gray-700 hover:bg-gray-50'"
            @click="$emit('set-mode', 'vertical')">
            Vertical
          </button>
          <button
            type="button"
            class="border border-gray-300 bg-white px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
            @click="$emit('set-mode', 'none')">
            Desativar
          </button>
        </div>

        <!-- Config horizontal só quando selecionado -->
        <div v-if="mode === 'horizontal'" class="flex items-center gap-2">
          <label class="text-sm text-body">Cor</label>
          <input
            v-model="form.color"
            type="color"
            class="h-7 w-9 border border-gray-300 border-solid bg-white p-0.5"
            @input="emitConfig" />
          <label class="text-sm text-body">Texto</label>
          <input
            v-model="form.label"
            type="text"
            maxlength="40"
            placeholder="Label"
            class="h-7 w-32 border border-gray-300 border-solid px-2 text-sm focus:border-blue-600 focus:outline-none"
            @input="emitConfig" />
        </div>

        <!-- Dica vertical -->
        <p v-if="mode === 'vertical'" class="text-sm text-body">
          Clique no gráfico para criar marca e linha vertical.
        </p>

        <!-- Marcações de apoio -->
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-sm font-medium text-body">Marcações:</span>
          <button
            type="button"
            class="border border-gray-300 border-solid bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            :disabled="markersCount === 0"
            @click="$emit('clear-markers')">
            Limpar marks ({{ markersCount }})
          </button>
          <button
            type="button"
            class="border border-red-200 border-solid bg-white px-2 py-1 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            :disabled="lines.length === 0"
            @click="$emit('clear-lines')">
            Limpar linhas ({{ lines.length }})
          </button>
        </div>

        <!-- Lista de linhas horizontais -->
        <div v-if="lines.length > 0" class="flex w-full flex-wrap items-center gap-1.5">
          <template v-for="(line, index) in lines" :key="`line-${index}-${line.value}`">
            <div class="flex items-center gap-1 border border-gray-200 border-solid bg-gray-50 px-2 py-0.5">
              <span class="h-2 w-2 shrink-0 border border-gray-300 border-solid" :style="{ backgroundColor: line.color }" />
              <span class="truncate text-sm text-body" :title="`Y: ${line.value.toFixed(2)}`">Y:{{ line.value.toFixed(2) }}</span>
              <span class="max-w-[60px] truncate text-sm text-body">{{ line.label || '—' }}</span>
              <button type="button" class="shrink-0 text-sm font-semibold text-red-600 hover:underline" @click="$emit('remove-line', index)">×</button>
            </div>
          </template>
        </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive } from 'vue'

export type ChartToolMode = 'none' | 'horizontal' | 'vertical'

export interface IChartReferenceLine {
  value: number
  color: string
  label: string
}

const props = defineProps<{
  lines: IChartReferenceLine[]
  mode: ChartToolMode
  markersCount?: number
}>()

const emit = defineEmits<{
  (e: 'set-mode', mode: ChartToolMode): void
  (e: 'update-horizontal-config', config: { color: string; label: string }): void
  (e: 'remove-line', index: number): void
  (e: 'clear-lines'): void
  (e: 'clear-markers'): void
}>()

const form = reactive<{ color: string; label: string }>({
  color: '#ef4444',
  label: '',
})

function emitConfig() {
  emit('update-horizontal-config', { color: form.color, label: form.label.trim() })
}
</script>

<style scoped>
.chart-toolbox {
  border-radius: 0;
  overflow: hidden;
}
</style>
